import { type Fight } from '@/entities/leaderboards/model/pvp.types';
import { type MarketItem } from '@/entities/market/model/market.types';
import { type Prize } from '@/entities/wheel/model/wheel.types';

import { type Game } from '@/shared/store/games-store/types';
import { type LocalizedText } from '@/shared/types/common';

import { type Wallet } from './wallet.types';

export interface WheelProfile {
  isWheelAvailable: boolean;
  ticketsCount: number;
  spinsCount: number;
  isOnboardingPassed: boolean;
  prizes: Prize[] | undefined;
  wheelTimerNext: number | null;
  wheelTimerUntil: number | null;
  isWheelNewbie: boolean;
  isWheelActive: boolean;
}

export enum ClickerUnit {
  XP = 'XP',
  PLAYCOIN = 'PLAYCOIN',
}

export interface ActiveClicker {
  activeTo: number | null;
  bestTotal: number;
  currentTotal: number;
  duration: number;
  teamBestTotal: number;
  type: ClickerUnit;
  marketItem: MarketItem | null;
}

export enum LeaderboardType {
  REFERRAL = 'REFERRAL',
  GAME = 'GAME',
}

export interface UserLeaderboardShort {
  leaderboardType?: LeaderboardType;
  place: number;
  placeUdx?: number;
  score: number;
  leagueNumber?: number;
  lastPlace: number;
  totalScore?: number;
  leaderboardId: number;
  hasMP5?: boolean;
}

export interface NewFeatureAlert {
  iconUrl: string;
  heading: LocalizedText;
  text: LocalizedText;
  actionText: LocalizedText;
  actionUrl: string;
  isModalVisible: boolean;
  version: number;
}

export interface XP {
  value: number | null;
  valueForLevelUp: number;
  level: number;
  grid?: XpLevel[];
}

export interface XpLevel {
  level: number;
  milestone: boolean;
  goldenReceived: true;
  prizes: LevelXpPrize[];
  goldenPrizes: LevelXpPrize[];
}

export enum LEVEL_XP_PRIZE {
  XP = 'XP',
  PLAYCOIN = 'PLAYCOIN',
  TICKET = 'TICKET',
  NOT = 'NOT',
  PREMIUM = 'PREMIUM',
  BOOSTER_PLAYCOIN = 'BOOSTER_PLAYCOIN',
}

export interface LevelXpPrize {
  type: LEVEL_XP_PRIZE;
  amount: number;
  duration?: string | null;
}

export enum CommunityStatus {
  SUBSCRIBED = 'subscribed',
  UNSUBSCRIBED = 'unsubscribed',
  WAS_SUBSCRIBED = 'wasSubscribed',
}

export interface UserAccess {
  spinXAvailable: boolean;
  spinXAvailableFor: number;
  noAds: boolean;
}

export interface UserType {
  avatar: string;
  access: UserAccess;
  username: string;
  firstName: string;
  lastName: string;
  telegramId: number;
  botStarted: boolean;
  gameSessionsCount: number;
  currentGameStarted: number;
  wallets: Array<Wallet>;
  gameOfTheDay?: Game;
  country: 'RU' | string;
  retentionPrize: string | undefined;
  retentionDay: number;
  newShopItemsCounter: number;
  tests: {
    wrapperOnboarding: boolean;
    search: boolean;
    leagueNewPrize: boolean;
    leagueNextRise: boolean;
    isNew: boolean;
    reborn?: boolean | null;
    hasNewFriends?: boolean | null;
    fromClicker: boolean;
    communityStatus?: CommunityStatus | null;
    modalTop?: boolean | null;
    onboarding?: boolean | null;
    /** Юзер в вайтлисте листинга $DECK */
    listingWL?: boolean | null;
    custom?: {
      isOnboardingCompleted?: boolean;
      ageConfirmation?: string;
      /**
       * Конец приёма заявок на листинг, epoch ms.
       * null — даты ещё нет, страница работает без таймера.
       * Дата в прошлом — раздел выключен целиком.
       * */
      listingPrepareEndDate?: number | null;
      /** Кошелёк, привязанный к листингу */
      listingWalletAddress?: string | null;
    };
  };
  avdNetworks: AVD_NETWORK[];
  wheel: WheelProfile;
  social:
    | {
        fb: boolean;
        vk: boolean;
      }
    | null
    | undefined;
  leaderboards?: UserLeaderboardShort[];
  referrerTgId?: number | null;
  clickerRemainingCount?: number;
  newFeatureAlert?: NewFeatureAlert | null;
  xp: XP;
  clickers: ActiveClicker[] | null;
  newTasksCounter: number;
  sbPlace: number;
  sbUpdates: boolean;
  friendsCount: number | null;
  fight: Fight | null;
}

export enum AVD_NETWORK {
  Y = 'Y',
  AG = 'AG',
  PD = 'PD',
  MT = 'MT',
}

export interface UserActions {
  hydrate: (
    partial: ((state: Partial<UserStore>) => Partial<UserStore>) | Partial<UserStore>
  ) => void;
  fetchUser: () => Promise<UserType | null>;
  getRetentionPrize: () => Promise<Prize | undefined>;
}

export interface UserState {
  user: UserType | undefined;
  retentionModalPrize: Prize | undefined;
  fight: Fight | undefined;
}

export interface UserStore extends UserState {
  actions: UserActions;
}

export enum AvatarSize {
  LARGE = 'large',
  MIDDLE = 'middle',
  SMALL = 'small',
}

export enum SocialMedia {
  FACEBOOK = 'FB',
  INSTAGRAM = 'IG',
  VK = 'VK',
}
